import React, { useEffect, useState } from "react";
import { HitApi } from "../../Api/ApiHit";
import { deleteClosing, searchClosing } from "../../constant/Constant";
import { useDispatch, useSelector } from "react-redux";
import { Edit2, Trash2, TrendingUp, DollarSign, CreditCard, Wallet, Calendar, Home, Zap, Users, Package, Gift } from "lucide-react";
import { setApiJson } from "../../redux/Actions/ApiAction";
import PageHeader from "../../PageHeader/PageHeader";
import AppTable from "../../components/AppTable/AppTable";
import FilterComponent from "../../components/Filter/FilterComponent";
import CloseModal from "../Dashboard/CloseModal";
import { setDataAction } from "../../redux/Actions/SetDataAction";
import {
    SET_CLOSING_DATA,
    SET_EXPENSE,
    SET_TODAY_BILL_DATA,
} from "../../redux/Reducers/ExpenseReducer";
import { calculateTotals } from "../../utils/utils";
import toast from "react-hot-toast";

const ClosingDays = () => {
    const ClosingReducer = useSelector((state) => state.ExpenseReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    const dispatch = useDispatch();

    const [modal, setModal] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [rawData, setRawData] = useState([]);

    // 🔹 Transformer: normalize API data
    const transformClosingData = (data = []) => {
        return data.map((item) => {
            // balance amounts
            const balances = [
                { label: "Cash", value: item.cashAmount || 0 },
                { label: "Upi", value: item.upiAmount || 0 },
                { label: "Card", value: item.cardAmount || 0 },
                { label: "Wallet", value: item.walletAmount || 0 },
            ];
            const balanceTotal = balances.reduce((sum, b) => sum + (b.value || 0), 0);
            balances.push({ label: "Total", value: balanceTotal });

            // expense breakdown for display only - keep original expense value intact
            const expenseBreakdown = [
                { label: "Incentive", value: item.incentive || 0 },
                { label: "Rent", value: item.rent || 0 },
                { label: "Electricity", value: item.electricity || 0 },
                { label: "Product Cost", value: item.productCost || 0 },
            ].filter((e) => e.value && e.value !== 0);

            return {
                ...item,
                grandTotal: [{ label: "Total Sales", value: item.grandTotal || 0 }],
                balanceAmounts: balances,
                expenseBreakdown: expenseBreakdown, // Keep breakdown separate
                // Keep original expense value for calculations
            };
        });
    };

    // Calculate summary metrics from raw data (before transformation)
    const calculateSummaryMetrics = (data = []) => {
        if (!data || data.length === 0) {
            return {
                totalSales: 0,
                totalExpenses: 0,
                netProfit: 0,
                totalCash: 0,
                totalCard: 0,
                totalUpi: 0,
                totalWallet: 0,
                totalRecords: 0,
                // Expense breakdowns
                totalRent: 0,
                totalElectricity: 0,
                totalIncentive: 0,
                totalProductCost: 0,
                totalSalary: 0
            };
        }

        const totalSales = data.reduce((sum, item) => sum + Number(item.grandTotal || 0), 0);
        const totalExpenses = data.reduce((sum, item) => sum + Number(item.expense || 0), 0);
        const totalCash = data.reduce((sum, item) => sum + Number(item.cashAmount || 0), 0);
        const totalCard = data.reduce((sum, item) => sum + Number(item.cardAmount || 0), 0);
        const totalUpi = data.reduce((sum, item) => sum + Number(item.upiAmount || 0), 0);
        const totalWallet = data.reduce((sum, item) => sum + Number(item.walletAmount || 0), 0);
        
        // Calculate expense breakdowns
        const totalRent = data.reduce((sum, item) => sum + Number(item.rent || 0), 0);
        const totalElectricity = data.reduce((sum, item) => sum + Number(item.electricity || 0), 0);
        const totalIncentive = data.reduce((sum, item) => sum + Number(item.incentive || 0), 0);
        const totalProductCost = data.reduce((sum, item) => sum + Number(item.productCost || 0), 0);
        
        // Calculate salary from expense if available, otherwise use a default calculation
        const totalSalary = data.reduce((sum, item) => {
            // If we have specific salary data, use it; otherwise estimate from total expenses
            if (item.salary) return sum + Number(item.salary);
            // Estimate salary as remaining expenses after known categories
            const knownExpenses = Number(item.rent || 0) + Number(item.electricity || 0) + 
                                Number(item.incentive || 0) + Number(item.productCost || 0);
            const remainingExpense = Number(item.expense || 0) - knownExpenses;
            return sum + Math.max(0, remainingExpense); // Only add if positive
        }, 0);
        
        const netProfit = totalSales - totalExpenses;
        
        return {
            totalSales,
            totalExpenses,
            netProfit,
            totalCash,
            totalCard,
            totalUpi,
            totalWallet,
            totalRecords: data.length,
            // Expense breakdowns
            totalRent,
            totalElectricity,
            totalIncentive,
            totalProductCost,
            totalSalary
        };
    };

    useEffect(() => {
        if (ClosingReducer?.closingData === null) {
            fetchData();
        }
    }, [modal]);

    const baseIndex =
        (ClosingReducer?.closingData?.page - 1) * TableDataReducer.pagination.limit;

    const fetchData = () => {
        setIsLoading(true);
        
        // Build date range filter
        let dateRangeFilter = {};
        if (startDate && endDate) {
            // Both dates provided - filter between range
            dateRangeFilter = {
                $gte: new Date(startDate).getTime(),
                $lte: new Date(endDate).getTime() + 24 * 60 * 60 * 1000 - 1,
            };
        } else if (startDate) {
            // Only start date - filter from start date onwards
            dateRangeFilter = {
                $gte: new Date(startDate).getTime(),
            };
        } else if (endDate) {
            // Only end date - filter up to end date
            dateRangeFilter = {
                $lte: new Date(endDate).getTime() + 24 * 60 * 60 * 1000 - 1,
            };
        }
        
        const json = {
            page: 1,
            limit: 50,
            search: {
                ...(statusFilter !== "all" && { status: statusFilter === "closed" }),
                ...(Object.keys(dateRangeFilter).length > 0 && {
                    createdAt: dateRangeFilter,
                }),
            },
        };

        HitApi(json, searchClosing)
            .then((res) => {
                const rawDocs = res?.data?.docs || [];
                setRawData(rawDocs); // Store raw data for summary calculations
                const transformed = transformClosingData(rawDocs);
                const newData = {
                    ...res?.data,
                    docs: transformed,
                };
                dispatch(setDataAction(newData, SET_CLOSING_DATA));
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching closing data:", error);
                toast.error("Failed to fetch closing data");
                setIsLoading(false);
            });
    };

    // 🔹 Trigger refetch when filters change
    useEffect(() => {
        fetchData();
    }, [statusFilter, startDate, endDate]);

    const renderMethodCell = (items) => {
        if (!items || items === 0 || (Array.isArray(items) && !items.length))
            return <span className="text-gray-500 text-sm">₹0.00</span>;

        if (typeof items === "number")
            return (
                <span className="font-semibold text-green-600">
                    ₹{items.toFixed(2)}
                </span>
            );
        if (typeof items === "string")
            return <span className="font-medium">{items}</span>;

        const renderItem = (item, i) => {
            const label = item.label || item.method;
            const value = item.value || item.amount;

            const getMethodColor = (method) => {
                switch (method?.toLowerCase()) {
                    case "cash":
                        return "text-green-600 bg-green-50";
                    case "card":
                        return "text-blue-600 bg-blue-50";
                    case "upi":
                        return "text-purple-600 bg-purple-50";
                    case "wallet":
                        return "text-orange-600 bg-orange-50";
                    case "total":
                        return "text-gray-800 bg-gray-100 font-bold";
                    default:
                        return "text-gray-600 bg-gray-50";
                }
            };

            return (
                <div
                    key={i}
                    className={`flex items-center justify-between px-2 py-1 rounded-md ${getMethodColor(
                        label
                    )} text-xs`}
                >
                    {label && <span className="font-medium">{label}:</span>}
                    {value !== undefined && (
                        <span>₹{Number(value).toFixed(2)}</span>
                    )}
                    {item.note && (
                        <span className="text-gray-500 text-xs ml-1">
                            ({item.note})
                        </span>
                    )}
                    {item.image && (
                        <img
                            src={item.image}
                            alt={item.note || "expense"}
                            className="w-5 h-5 rounded ml-1"
                        />
                    )}
                </div>
            );
        };

        if (Array.isArray(items))
            return (
                <div className="space-y-1 max-w-40">
                    {items.map((item, i) =>
                        typeof item === "object" ? (
                            renderItem(item, i)
                        ) : (
                            <div key={i} className="text-xs font-medium">
                                ₹{Number(item).toFixed(2)}
                            </div>
                        )
                    )}
                </div>
            );

        if (typeof items === "object") return renderItem(items);

        return <span className="text-sm">{String(items)}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString();
    };


    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            align: "center",
            width: "60px",
            render: (_, __, index) => (
                <div className="text-center font-medium">{baseIndex + index + 1}</div>
            ),
        },
        {
            title: "Date",
            key: "createdAt",
            render: (value) => (
                <div className="text-sm">
                    <div className="font-medium text-gray-900">{formatDate(value)}</div>
                    <div className="text-gray-500">{new Date(value).toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit'
                    })}</div>
                </div>
            ),
        },
        {
            title: "Total Sales",
            key: "grandTotal",
            render: (value, row) => {
                // Handle both transformed array format and direct value
                let amount = 0;
                if (Array.isArray(value) && value.length > 0) {
                    amount = value[0].value;
                } else if (typeof value === 'number') {
                    amount = value;
                } else if (row && row.grandTotal) {
                    amount = Array.isArray(row.grandTotal) ? row.grandTotal[0]?.value : row.grandTotal;
                }
                return (
                    <div className="font-semibold text-green-600 text-lg">
                        ₹{Number(amount || 0).toLocaleString()}
                    </div>
                );
            },
        },
        {
            title: "Payment Methods",
            key: "balanceAmounts",
            render: (value) => {
                if (!value || !Array.isArray(value)) {
                    return <span className="text-gray-500 text-sm">No data</span>;
                }

                // Filter out the "Total" entry and only show payment methods with values > 0
                const paymentMethods = value.filter(item => 
                    item.label !== "Total" && item.value > 0
                );

                if (paymentMethods.length === 0) {
                    return <span className="text-gray-500 text-sm">₹0.00</span>;
                }

                return (
                    <div className="flex flex-wrap gap-1">
                        {paymentMethods.map((item, i) => {
                            const getMethodStyle = (method) => {
                                switch (method?.toLowerCase()) {
                                    case "cash":
                                        return "bg-green-100 text-green-700 border-green-200";
                                    case "card":
                                        return "bg-blue-100 text-blue-700 border-blue-200";
                                    case "upi":
                                        return "bg-purple-100 text-purple-700 border-purple-200";
                                    case "wallet":
                                        return "bg-orange-100 text-orange-700 border-orange-200";
                                    default:
                                        return "bg-gray-100 text-gray-700 border-gray-200";
                                }
                            };

                            return (
                                <div
                                    key={i}
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getMethodStyle(item.label)}`}
                                >
                                    <span className="font-semibold">{item.label}:</span>
                                    <span className="ml-1">₹{Number(item.value).toLocaleString()}</span>
                                </div>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            title: "Expenses",
            key: "expense",
            render: (value, row) => {
                // Use the original expense value from the raw data, not the transformed array
                let amount = 0;
                
                // First try to get the raw expense value directly
                if (typeof row.expense === 'number') {
                    amount = row.expense;
                } else if (typeof value === 'number') {
                    amount = value;
                } else if (Array.isArray(value)) {
                    // Only sum array if we don't have a direct value - this is for display breakdown
                    // But for total calculation, we should use the actual expense amount
                    amount = row.expense || value.reduce((sum, item) => sum + (item.value || 0), 0);
                }
                
                return (
                    <div className="font-semibold text-red-600 text-lg">
                        ₹{Number(amount || 0).toLocaleString()}
                    </div>
                );
            },
        },
        {
            title: "Net Profit",
            key: "netProfit",
            render: (_, row) => {
                // Calculate profit from row data, prioritizing original values
                let sales = 0;
                let expenses = 0;
                
                // Extract sales amount - prioritize original grandTotal
                if (typeof row.grandTotal === 'number') {
                    sales = row.grandTotal;
                } else if (Array.isArray(row.grandTotal) && row.grandTotal.length > 0) {
                    sales = row.grandTotal[0].value || 0;
                }
                
                // Extract expense amount - prioritize original expense value
                if (typeof row.expense === 'number') {
                    expenses = row.expense;
                } else if (Array.isArray(row.expense)) {
                    expenses = row.expense.reduce((sum, item) => sum + (item.value || 0), 0);
                }
                
                const profit = sales - expenses;
                return (
                    <div className={`font-semibold text-lg ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ₹{Number(profit || 0).toLocaleString()}
                    </div>
                );
            },
        },
        {
            title: "Status",
            key: "status",
            render: (value) => {
                const status = value !== true ? "Pending" : "Closed";
                return (
                    <span
                        className={`px-3 py-1 rounded-full font-semibold text-sm ${
                            status === "Pending"
                                ? "bg-red-100 text-red-700"
                                : "bg-green-100 text-green-700"
                        }`}
                    >
                        {status}
                    </span>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, row, index) => (
                <div className="flex items-center justify-center">
                    {!row?.status && (
                        <button
                            onClick={() => onClickEdit(index, row)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Edit"
                        >
                            <Edit2 size={16} />
                        </button>
                    )}
                    {row?.status && (
                        <span className="text-gray-400 text-sm">No actions</span>
                    )}
                </div>
            ),
        },
    ];

    const onClickEdit = (index, row) => {
        setModal(index);
        const compileJson = {
            ...ClosingReducer?.closingData?.docs?.[index],
            totalExpense: calculateTotals(
                ClosingReducer?.closingData?.docs?.[index]?.expense
            ),
        };
        dispatch(setDataAction(compileJson, SET_TODAY_BILL_DATA));
        dispatch(
            setDataAction(
                ClosingReducer?.closingData?.docs?.[index]?.expense,
                SET_EXPENSE
            )
        );
    };

    const summaryMetrics = calculateSummaryMetrics(rawData);

    const SummaryCard = ({ title, value, icon: Icon, color, subtitle, isNumber = true }) => (
        <div className={`bg-white rounded-xl p-6 border-l-4 ${color}`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">
                        {isNumber ? `₹${Number(value || 0).toLocaleString()}` : value}
                    </p>
                    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                </div>
                <div className={`p-3 rounded-full ${color.replace('border-l-', 'bg-').replace('-500', '-100')}`}>
                    <Icon className={`h-6 w-6 ${color.replace('border-l-', 'text-')}`} />
                </div>
            </div>
        </div>
    );

    const PaymentMethodCard = ({ method, amount, icon: Icon, color }) => (
        <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full ${color}`}>
                    <Icon className="h-5 w-5 text-white" />
                </div>
                <div>
                    <p className="text-sm font-medium text-gray-600">{method}</p>
                    <p className="text-lg font-bold text-gray-900">₹{Number(amount || 0).toLocaleString()}</p>
                </div>
            </div>
        </div>
    );

    // Loading Overlay Component
    const LoadingOverlay = () => (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="text-gray-700 font-medium">Loading closing data...</p>
            </div>
        </div>
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {isLoading && <LoadingOverlay />}
            
            <div className="mb-6 flex justify-between items-start">
                <PageHeader title="Closing Days" description="Daily business closure overview and financial summary" />
                {/* Filters - Moved to top right */}
                <FilterComponent
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    isLoading={isLoading}
                    className="rounded-xl"
                />
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <SummaryCard
                    title="Total Sales"
                    value={summaryMetrics.totalSales}
                    icon={TrendingUp}
                    color="border-l-green-500"
                    subtitle={`From ${summaryMetrics.totalRecords} closing${summaryMetrics.totalRecords !== 1 ? 's' : ''}`}
                />
                <SummaryCard
                    title="Total Expenses"
                    value={summaryMetrics.totalExpenses}
                    icon={DollarSign}
                    color="border-l-red-500"
                    subtitle="All operational costs"
                />
                <SummaryCard
                    title="Net Profit"
                    value={summaryMetrics.netProfit}
                    icon={TrendingUp}
                    color={summaryMetrics.netProfit >= 0 ? "border-l-blue-500" : "border-l-red-500"}
                    subtitle={summaryMetrics.netProfit >= 0 ? "Profitable" : "Loss"}
                />
                <SummaryCard
                    title="Total Records"
                    value={summaryMetrics.totalRecords}
                    icon={Calendar}
                    color="border-l-purple-500"
                    subtitle="Closing entries"
                    isNumber={false}
                />
            </div>

            {/* Payment Methods Breakdown */}
            <div className="bg-white rounded-xl p-6 border mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    Payment Methods Breakdown
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <PaymentMethodCard
                        method="Cash"
                        amount={summaryMetrics.totalCash}
                        icon={DollarSign}
                        color="bg-green-500"
                    />
                    <PaymentMethodCard
                        method="Card"
                        amount={summaryMetrics.totalCard}
                        icon={CreditCard}
                        color="bg-blue-500"
                    />
                    <PaymentMethodCard
                        method="UPI"
                        amount={summaryMetrics.totalUpi}
                        icon={Wallet}
                        color="bg-purple-500"
                    />
                    <PaymentMethodCard
                        method="Wallet"
                        amount={summaryMetrics.totalWallet}
                        icon={Wallet}
                        color="bg-orange-500"
                    />
                </div>
            </div>

            {/* Expense Types Breakdown */}
            <div className="bg-white rounded-xl p-6 border mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Expense Categories Breakdown
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <PaymentMethodCard
                        method="Rent"
                        amount={summaryMetrics.totalRent}
                        icon={Home}
                        color="bg-purple-500"
                    />
                    <PaymentMethodCard
                        method="Electricity"
                        amount={summaryMetrics.totalElectricity}
                        icon={Zap}
                        color="bg-yellow-500"
                    />
                    <PaymentMethodCard
                        method="Incentives"
                        amount={summaryMetrics.totalIncentive}
                        icon={Gift}
                        color="bg-orange-500"
                    />
                    <PaymentMethodCard
                        method="Product Cost"
                        amount={summaryMetrics.totalProductCost}
                        icon={Package}
                        color="bg-teal-500"
                    />
                    <PaymentMethodCard
                        method="Salary"
                        amount={summaryMetrics.totalSalary}
                        icon={Users}
                        color="bg-slate-500"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Detailed Closing Records</h3>
                </div>
                <AppTable
                    TH={tableHeaders}
                    TD={ClosingReducer?.closingData?.docs || []}
                    isLoading={isLoading}
                    emptyMessage="No closing records found"
                />
            </div>

            {modal !== false && (
                <CloseModal
                    isEditable={true}
                    editStart={"editStart"}
                    editEnd={"editEnd"}
                    modal={true}
                    setModal={setModal}
                />
            )}
        </div>
    );
};

export default ClosingDays;
